import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Sparkles } from 'lucide-react';
import axiosInstance from '../api/axios';
import { COLLECTION_BOXES } from '../data/collections';
import useHairCollections from '../hooks/useHairCollections';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BoxFinderQuiz from '../components/BoxFinderQuiz';
import { formatEUR } from '../utils/format';

const Pill = ({ label, palette }) => (
  <span
    className="inline-block text-xs px-3 py-1"
    style={{
      backgroundColor: `${palette.accent}1F`,
      color: palette.text,
      border: `1px solid ${palette.accent}55`,
    }}
  >
    {label}
  </span>
);

export const CollectionsOverview = () => {
  const { collections } = useHairCollections();
  const location = useLocation();
  // Default to seasonal (premium hero) when nothing specific is requested
  const initialCat = location?.state?.activeCategory || 'limited-seasonal';
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(initialCat);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get('/hair-collections/categories')
      .then((res) => {
        if (cancelled) return;
        const cats = Array.isArray(res.data) ? res.data.filter((c) => c.is_active !== false) : [];
        setCategories(cats);
        // If state.activeCategory is set and exists, honor it.
        // Otherwise fall back to first category.
        if (cats.length && !cats.find((c) => c.slug === activeCategory)) {
          setActiveCategory(cats[0].slug);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const showTabs = categories.length > 1;
  const activeCat = categories.find((c) => c.slug === activeCategory);

  const filtered = useMemo(() => {
    if (!showTabs) return collections;
    return collections.filter((c) => (c.category || 'limited-hair') === activeCategory);
  }, [collections, activeCategory, showTabs]);

  // The page-level H1 stays "Limited Collections" — the active-tab name lives
  // in the subtitle below so users can see which category is selected.
  const headerTitle = 'Limited Collections';
  const activeCatName = showTabs && activeCat ? activeCat.name : null;
  const headerSubtitle = showTabs && activeCat && activeCat.description
    ? activeCat.description
    : 'Kuratierte Boxen rund um Haar, Beauty, Home, Study & saisonale Highlights. Limitierte Stückzahlen — solange verfügbar.';

  return (
  <div className="App flex flex-col min-h-screen">
    <Navbar />
    <main className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <p className="text-[11px] tracking-[5px] uppercase mb-2 text-[#C4A084]">✨ Kollektionen</p>
        <h1 className="text-3xl md:text-5xl font-bold text-[#5D4E3C] mb-3" style={{ fontFamily: 'Georgia, serif' }}>
          {headerTitle}
        </h1>
        <div className="w-12 h-[2px] mb-4" style={{ backgroundColor: '#C4A084' }} />
        {activeCatName && (
          <p className="text-[12px] tracking-[3px] uppercase font-semibold mb-2" style={{ color: activeCat?.accent_color || '#C4A084' }}>
            {activeCat?.emoji} {activeCatName}
          </p>
        )}
        <p className="text-[#5C4E3D] max-w-2xl mb-8">{headerSubtitle}</p>

        {showTabs && (
          <div className="flex items-center gap-1 mb-8 border-b border-[#E8DCC8] overflow-x-auto" data-testid="collections-overview-tabs">
            {categories.map((cat) => {
              const isActive = activeCategory === cat.slug;
              return (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`shrink-0 px-4 py-2.5 text-sm transition-all border-b-2 -mb-px flex items-center gap-2 ${
                    isActive
                      ? 'text-[#5D4E3C] font-semibold'
                      : 'border-transparent text-[#9C8C7A] hover:text-[#5D4E3C]'
                  }`}
                  style={isActive ? { borderBottomColor: cat.accent_color || '#5D4E3C' } : { borderBottomColor: 'transparent' }}
                  data-testid={`overview-tab-${cat.slug}`}
                >
                  <span>{cat.emoji}</span>
                  <span>{cat.name}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {filtered.map((c) => (
            <Link
              key={c.slug}
              to={`/collections/${c.slug}`}
              className="group relative aspect-[4/3] overflow-hidden transition-transform hover:-translate-y-1"
              style={{ backgroundColor: c.palette.bg, color: c.palette.text }}
            >
              <div className="absolute inset-0 p-5 md:p-7 flex flex-col">
                <div className="text-3xl md:text-4xl mb-1">{c.emoji}</div>
                <p className="text-[10px] tracking-[3px] uppercase font-semibold opacity-80" style={{ color: c.palette.accent }}>
                  {c.style}
                </p>
                <h3 className="text-xl md:text-2xl font-bold mt-1" style={{ fontFamily: 'Georgia, serif' }}>
                  {c.name}
                </h3>
                <p className="text-xs md:text-sm opacity-80 mt-2 leading-relaxed">{c.tagline}</p>
                <div className="mt-auto inline-flex items-center gap-1 text-[11px] tracking-[2px] uppercase font-semibold" style={{ color: c.palette.accent }}>
                  Entdecken <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Box-Finder Quiz */}
        <div className="mt-12 md:mt-16">
          <BoxFinderQuiz />
        </div>
      </div>
    </main>
    <Footer />
  </div>
  );
};

const STUDY_BOXES = [
  { slug: 'small', emoji: '🟣', name: 'Small Box', price: 14.99, items: '5–7 Produkte',
    total: 'Fokus: Basics + 1 Highlight',
    description: 'Ein kompaktes, perfekt kuratiertes Set für den Einstieg in deine Edition.' },
  { slug: 'medium', emoji: '🔵', name: 'Medium Box', price: 22.99, items: '10–12 Produkte',
    total: 'Mix aus Schreibwaren · Organisation · Extras',
    description: 'Die große Variante mit mehr Vielfalt — perfekt als Geschenk oder Komplett-Set.' },
];

const CURATED_BOXES = [
  { slug: 'small', emoji: '🟣', name: 'Small Collection', price: 14.90, items: '5–7 Produkte',
    total: 'Perfekt als kleine Überraschung',
    description: 'Ideal als Geschenk oder Aufmerksamkeit.' },
  { slug: 'medium', emoji: '🔵', name: 'Medium Collection', price: 22.90, items: '10–12 Produkte',
    total: 'Umfangreichere Zusammenstellung',
    description: 'Mehr Vielfalt innerhalb des gewählten Themas.' },
];

const SEASONAL_BOXES = [
  { slug: 'small', emoji: '🎀', name: 'Small Saison-Box', price: 14.90, items: '5–7 Produkte',
    total: 'Vereint das Beste der Saison',
    description: 'Eine kompakte Überraschung im Look der gewählten Jahreszeit.' },
  { slug: 'large', emoji: '👑', name: 'Large Saison-Box', price: 29.99, items: '8–12 Produkte · inkl. Statement-Stück',
    total: 'Premium-Mix mit Highlight-Artikel (z.B. Tasche oder XL-Wohndeko)',
    description: 'Die große Premium-Edition: kuratierte Lieblinge & ein Saison-Statement.' },
];

const CATEGORY_CONFIG = {
  'limited-study': {
    boxes: STUDY_BOXES,
    heroBadge: '📚 Limited Edition · Study Box',
    contentLabel: 'Mögliche Inhalte',
    boxesHeader: '📦 Wähle deine Boxgröße',
    boxesSubheader: 'Individuell kuratiert · Jede Box ist einzigartig',
    boxesNote: 'Jede Box wird passend zur gewählten Edition individuell zusammengestellt',
    footerNote: '👉 Inhalte variieren je nach Edition und Verfügbarkeit · 👉 Jede Bestellung ist eine Überraschung im gewählten Stil',
    explainerTitle: 'Eine kuratierte Study-Experience im Stil deiner Wahl',
    explainerText: 'Du kaufst kein festes Set, sondern eine individuell kuratierte Überraschungsbox. Wähle deine Edition und Boxgröße — wir stellen deine Box aus passenden Schreibwaren, Notizmaterialien und kleinen Accessoires im gewählten Stil zusammen.',
  },
  'limited-beauty': {
    boxes: CURATED_BOXES,
    heroBadge: '💄 Limited Edition · Beauty Box',
    contentLabel: 'Mögliche Inhalte',
    boxesHeader: '📦 Wähle deine Boxgröße',
    boxesSubheader: 'Individuell kuratiert · Jede Box ist einzigartig',
    boxesNote: 'Jede Beauty-Collection wird passend zum gewählten Thema kuratiert',
    footerNote: '👉 Die Produktzusammenstellung kann variieren · 👉 Jede Bestellung ist eine Überraschung im gewählten Stil',
    explainerTitle: 'Eine kuratierte Beauty-Experience im Stil deiner Wahl',
    explainerText: 'Sorgfältig zusammengestellte Überraschungsboxen rund um Beauty, Selfcare und schöne Alltagsmomente. Jede Box wird passend zum gewählten Thema kuratiert — die enthaltenen Produkte variieren und machen jede Box einzigartig.',
  },
  'limited-home': {
    boxes: CURATED_BOXES,
    heroBadge: '🏡 Limited Edition · Home Box',
    contentLabel: 'Mögliche Inhalte',
    boxesHeader: '📦 Wähle deine Boxgröße',
    boxesSubheader: 'Individuell kuratiert · Jede Box ist einzigartig',
    boxesNote: 'Jede Home-Collection wird passend zum Wohngefühl kuratiert',
    footerNote: '👉 Die Produktzusammenstellung variiert · 👉 Jede Box ist eine Überraschung passend zum Thema',
    explainerTitle: 'Eine kuratierte Home-Experience im Stil deiner Wahl',
    explainerText: 'Bringt kleine besondere Details in den Alltag und schafft eine angenehme Atmosphäre im eigenen Zuhause. Jede Collection orientiert sich an einem bestimmten Wohngefühl — die enthaltenen Produkte variieren und machen jede Box zu einer individuellen Überraschung.',
  },
  'limited-seasonal': {
    boxes: SEASONAL_BOXES,
    heroBadge: '🌿 Limited Premium · Saison-Edition',
    contentLabel: 'Mögliche Saison-Schätze',
    boxesHeader: '📦 Wähle deine Saison-Box',
    boxesSubheader: 'Premium-Linie · vereint Haar · Beauty · Home · Study',
    boxesNote: 'Jede Saison-Box ist eine kuratierte Überraschung im Stil der Jahreszeit',
    footerNote: '👉 Inhalte variieren je nach Saison · 👉 Large enthält ein größeres Statement-Stück (z.B. Tasche)',
    explainerTitle: 'Die Premium-Linie: Vier Jahreszeiten · ein Lebensgefühl',
    explainerText: 'Unsere Saison-Editionen sind die Krönung der Reihe — eine handverlesene Mischung aus allen Welten (Haar, Beauty, Home, Study), perfekt abgestimmt auf die Stimmung der Jahreszeit. Die Large-Variante darf auch ein größeres Statement-Stück wie eine Tasche oder Premium-Wohndeko enthalten.',
  },
};

const CollectionDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { collections, loading } = useHairCollections();
  const collection = collections.find((c) => c.slug === slug);

  if (loading && !collection) {
    return (
      <div className="App flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-1 max-w-4xl mx-auto px-4 py-16 text-center">
          <p className="text-[#9C8C7A]">Lädt …</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="App flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-1 max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl text-[#5D4E3C] mb-4">Collection nicht gefunden</h1>
          <Link to="/collections" className="text-[#C4A084] underline">Zurück zur Übersicht</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const p = collection.palette;
  const cat = collection.category || 'limited-hair';
  const cfg = CATEGORY_CONFIG[cat];
  const isCurated = Boolean(cfg);
  const contentLabel = cfg?.contentLabel || 'Designs';
  const heroBadge = cfg?.heroBadge || '✨ Limited Edition · Collection';
  const boxesData = cfg?.boxes || COLLECTION_BOXES;
  const boxesHeader = cfg?.boxesHeader || '📦 Deine rundlichen Kollektionsboxen';
  const boxesSubheader = cfg?.boxesSubheader || 'In limitierter Stückzahl — solange verfügbar';
  const boxesNote = cfg?.boxesNote || 'Jede Box ist eine rundliche Geschenkbox im Themen-Stil';

  return (
    <div className="App flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1">
        {/* Hero */}
        <section
          className="px-4 sm:px-6 lg:px-8 py-12 md:py-20 relative overflow-hidden"
          style={{ backgroundColor: p.bg, color: p.text }}
          data-testid={`collection-hero-${collection.slug}`}
        >
          <div className="max-w-5xl mx-auto">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-sm mb-6 hover:opacity-70"
              style={{ color: p.text }}
              data-testid="collection-back"
            >
              <ArrowLeft className="h-4 w-4" />
              Zurück
            </button>

            <p className="text-[11px] tracking-[5px] uppercase mb-2" style={{ color: p.accent }}>
              {heroBadge}
            </p>
            <h1 className="text-4xl md:text-6xl font-bold mb-3 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
              {collection.emoji} {collection.name}
            </h1>
            <div className="w-14 h-[2px] mb-5" style={{ backgroundColor: p.accent }} />
            <p className="text-base md:text-lg italic max-w-2xl opacity-95 leading-relaxed">
              {collection.tagline}
            </p>
          </div>
          <Sparkles className="absolute top-6 right-6 h-16 w-16 opacity-10" />
        </section>

        {/* Detail blocks */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 md:py-16 bg-white">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
            <div>
              <p className="text-[11px] tracking-[4px] uppercase mb-3" style={{ color: p.accent }}>Stil</p>
              <p className="text-xl md:text-2xl mb-8 text-[#3E2723]" style={{ fontFamily: 'Georgia, serif' }}>
                {collection.style}
              </p>

              <p className="text-[11px] tracking-[4px] uppercase mb-3" style={{ color: p.accent }}>Farbwelt</p>
              <div className="flex flex-wrap gap-2 mb-8">
                {collection.colors.map((c) => <Pill key={c} label={c} palette={p} />)}
              </div>

              <p className="text-[11px] tracking-[4px] uppercase mb-3" style={{ color: p.accent }}>{contentLabel}</p>
              <div className="flex flex-wrap gap-2 mb-8">
                {collection.designs.map((d) => <Pill key={d} label={d} palette={p} />)}
              </div>
            </div>

            <div>
              <p className="text-[11px] tracking-[4px] uppercase mb-3" style={{ color: p.accent }}>Wirkung</p>
              <div className="flex flex-wrap gap-2 mb-8">
                {collection.mood.map((m) => <Pill key={m} label={m} palette={p} />)}
              </div>

              <p className="text-[11px] tracking-[4px] uppercase mb-3" style={{ color: p.accent }}>Styling-Tipp</p>
              <p className="text-sm md:text-base text-[#5C4E3D] mb-8 leading-relaxed italic">
                {collection.stylingTip}
              </p>

              {collection.productNames && (
                <>
                  <p className="text-[11px] tracking-[4px] uppercase mb-3" style={{ color: p.accent }}>Produkte dieser Collection</p>
                  <ul className="text-sm text-[#3E2723] space-y-1">
                    {collection.productNames.map((n) => <li key={n}>· {n}</li>)}
                  </ul>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Mystery-Box explainer (curated only) */}
        {isCurated && (
          <section className="px-4 sm:px-6 lg:px-8 py-10" style={{ backgroundColor: p.bg, color: p.text }}>
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-[11px] tracking-[5px] uppercase mb-2" style={{ color: p.accent }}>
                🎁 So funktioniert&apos;s
              </p>
              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                {cfg.explainerTitle}
              </h2>
              <p className="text-sm md:text-base opacity-90 leading-relaxed mb-4 max-w-2xl mx-auto">
                {cfg.explainerText}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl mx-auto mt-6 text-sm opacity-95">
                <div>✨ Jede Box ist einzigartig</div>
                <div>✨ Individuell kuratiert</div>
                <div>✨ Kein Paket ist identisch</div>
              </div>
            </div>
          </section>
        )}

        {/* Boxen */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 md:py-14" style={{ backgroundColor: '#FAF5EE' }}>
          <div className="max-w-5xl mx-auto">
            <p className="text-[11px] tracking-[4px] uppercase mb-2 text-[#C4A084] text-center">{boxesHeader}</p>
            <h2 className="text-2xl md:text-3xl text-[#5D4E3C] text-center mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              Sichere dir deine {collection.name}
            </h2>
            <p className="text-center text-sm text-[#9C8C7A] mb-2">{boxesSubheader}</p>
            <p className="text-center text-xs text-[#9C8C7A] italic mb-8">{boxesNote}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {boxesData.map((b) => (
                <div key={b.slug} className="bg-white border border-[#E8DCC8] p-6 md:p-8 flex flex-col">
                  <div className="text-3xl mb-1">{b.emoji}</div>
                  <h3 className="text-xl font-bold text-[#5D4E3C] mb-1" style={{ fontFamily: 'Georgia, serif' }}>
                    {b.name}
                  </h3>
                  <p className="text-2xl font-bold mb-3" style={{ color: p.accent }}>{formatEUR(b.price)}</p>
                  <p className="text-sm text-[#5C4E3D] mb-2">{b.items}</p>
                  <p className="text-xs text-[#9C8C7A] mb-3">{b.total}</p>
                  <p className="text-sm italic text-[#5D4E3C] mt-auto">{b.description}</p>
                </div>
              ))}
            </div>
            {!isCurated && (
              <div className="text-center mt-8 text-xs text-[#9C8C7A] italic">
                ⚠️ Limitierte Stückzahl · ⚠️ Nur solange verfügbar · ⚠️ Neue Collections ersetzen ausverkaufte
              </div>
            )}
            {isCurated && (
              <div className="text-center mt-8 text-xs text-[#9C8C7A] italic">
                {cfg.footerNote}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CollectionDetail;
